/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, forwardRef } from 'react';

import { Col } from '@design-system/components/grid/grid';
import { IconButton } from '@design-system/components/button/iconButton';
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons';
import { formatTime, parseTime } from './format-time';
import * as styles from './TimeInputWithControls.css';

const DEFAULT_TIME = '00:00';
const TIME_INCREMENT_SECONDS = 10;

type TimeInputWithControlsProps = {
  value?: string | number;
  onChange?: (e: { target: { value: number } }) => void;
  name?: string;
  [key: string]: any;
};

export const TimeInputWithControls = forwardRef<
  HTMLInputElement,
  TimeInputWithControlsProps
>((props, ref) => {
  const { value, onChange, ...rest } = props;
  const [displayValue, setDisplayValue] = useState(DEFAULT_TIME);

  useEffect(() => {
    setDisplayValue(
      value !== undefined ? formatTime(String(value)) : DEFAULT_TIME,
    );
  }, [value]);

  const handleTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setDisplayValue(e.target.value);
  };

  const handleTimeBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const formattedValue = formatTime(e.target.value);
    setDisplayValue(formattedValue);
    notifyChange(parseTime(e.target.value));
  };

  const notifyChange = (seconds: number) => {
    onChange?.({ target: { value: seconds } });
  };

  const adjustTime = (delta: number) => {
    const currentSeconds = parseTime(displayValue);
    const newSeconds = Math.max(0, currentSeconds + delta);
    const newValue = formatTime(newSeconds.toString());

    setDisplayValue(newValue);
    notifyChange(newSeconds);
  };

  const incrementTime = () => adjustTime(TIME_INCREMENT_SECONDS);
  const decrementTime = () => adjustTime(-TIME_INCREMENT_SECONDS);

  return (
    <Col>
      <div className={styles.timeInputWrapper}>
        <IconButton
          className={styles.buttonStyle}
          icon={faMinus}
          onClick={decrementTime}
          variant="filled"
          color="primaryTonal"
        />
        <input
          {...rest}
          type="text"
          className={styles.timeInput}
          value={displayValue}
          onChange={handleTimeChange}
          onBlur={handleTimeBlur}
          placeholder="00:00"
          pattern="[0-5][0-9]:[0-5][0-9]"
          maxLength={5}
          ref={ref}
        />
        <IconButton
          className={styles.buttonStyle}
          icon={faPlus}
          onClick={incrementTime}
          variant="filled"
          color="primaryTonal"
        />
      </div>
    </Col>
  );
});
